import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  AccountUserOnAccount,
  AccountUserResponse,
  UserResponse,
} from "@adva/types";

// Account-scoped user management hooks (ADV-1029). Mirror `use-admin.ts` in
// shape but hit `/core/account/users/*` so Owner and Admin (both of which
// satisfy `requireAbility("update", "Account")`) can manage their own
// account's members without the `super_admin`-only `/admin/*` gate.
//
// The platform super-admin pages at `/settings/adva/*` keep using
// `use-admin.ts` — those endpoints intentionally stay cross-account.

// Server-side response shape from GET /core/account/users — keyed `members`
// per ListAccountUsersResponseSchema in @adva/types.
interface ServerListResponse {
  members: AccountUserResponse[];
  total: number;
}

// Client-side shape consumed by Members page — keyed `users` so the same
// page can work against `useAdminAccountUsers` (super-admin cross-account)
// and `useAccountMembers` (tenant-scoped) without conditional access.
interface AccountUsersListResponse {
  users: AccountUserOnAccount[];
  total: number;
}

interface ResendInviteResponse {
  message: string;
  email: string;
}

interface ResetPasswordResponse {
  message: string;
  link_sent: boolean;
}

interface SuspendResponse {
  message: string;
  suspended: boolean;
}

interface UpdateRoleInput {
  account_role?: string;
  business_assignments?: {
    business_id: string;
    role: string;
    end_user_role?: string | null;
  }[];
}

interface AccountUserDetails extends AccountUserOnAccount {
  auth_user_id: string | null;
  email_confirmed_at: string | null;
  banned: boolean;
  business_assignments: {
    business_id: string;
    business_name: string;
    role: string | null;
  }[];
}

const ACCOUNT_USERS_KEY = ["account-users"] as const;

function invalidateAccountUsers(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: ACCOUNT_USERS_KEY });
}

// ─── Members list ───────────────────────────────────────────────────────────

export function useAccountMembers() {
  return useQuery({
    queryKey: ACCOUNT_USERS_KEY,
    queryFn: async (): Promise<AccountUsersListResponse> => {
      const body = await api.get<ServerListResponse>(`/core/account/users`);
      // Remap server's `members` key to the page-facing `users` key, and cast
      // to AccountUserOnAccount — the server returns AccountUserResponse,
      // which is a structural subset (AccountUserOnAccount adds only the
      // optional `is_stale_invite` flag the Members page does not consume).
      return {
        users: body.members as AccountUserOnAccount[],
        total: body.total,
      };
    },
  });
}

// ─── Invite admin (account-scoped) ──────────────────────────────────────────

interface AccountInviteAdminData {
  email: string;
  first_name?: string;
  last_name?: string;
  force_link_existing_auth_user_id?: string;
}

interface AccountInviteAdminResponse {
  user: UserResponse;
}

export function useAccountInviteAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AccountInviteAdminData) =>
      api.post<AccountInviteAdminResponse>(
        `/core/account/users/invite-admin`,
        data,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

// ─── Per-user actions ───────────────────────────────────────────────────────

export function useAccountResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<ResendInviteResponse>(
        `/core/account/users/${accountUserId}/resend-invite`,
        undefined,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<ResetPasswordResponse>(
        `/core/account/users/${accountUserId}/reset-password`,
        undefined,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountSuspendUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<SuspendResponse>(
        `/core/account/users/${accountUserId}/suspend`,
        undefined,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountUnsuspendUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<SuspendResponse>(
        `/core/account/users/${accountUserId}/unsuspend`,
        undefined,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: UpdateRoleInput & { userId: string }) =>
      api.patch<AccountUserDetails>(
        `/core/account/users/${userId}/role`,
        input,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountRemoveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<{ message: string }>(
        `/core/account/users/${accountUserId}/remove`,
        undefined,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}

export function useAccountDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.delete<{ message: string }>(
        `/core/account/users/${accountUserId}/hard-delete`,
      ),
    onSuccess: () => invalidateAccountUsers(queryClient),
  });
}
